import {
  LitElement,
  PropertyValueMap,
  PropertyValues,
  html,
  nothing,
} from "lit";
import { customElement, property, query, state } from "lit/decorators.js";

import { AuthController } from "../controllers/auth-controller";
import {
  sendEventUserLoggedOut,
  sendEventMyAchievementsClick,
  sendEventMyFavoritesClick,
  sendEventMyProfileClick,
  sendEventTriggerUserMenu,
  sendEventMyEssaysClick,
  sendEventMySubscriptionClick,
} from "../utils/analytics";
import { NotificationsController } from "../controllers/notifications-controller";
import { classMap } from "lit/directives/class-map.js";
import { SubscriptionController } from "../controllers/subscription-controller";
import { trackClickEvent } from "../../events/clicks-tracker";
import { showLoginModal } from "../utils/loginModal";

@customElement("profile-button")
export class ProfileButton extends LitElement {
  authController = new AuthController(this);
  subscriptionController = new SubscriptionController(this);
  notificationsController = new NotificationsController(
    this,
    this.authController
  );

  createRenderRoot() {
    return this;
  }

  @property({ type: Boolean }) isMobileMenu = false;

  @state() showMenu = false;
  @query("button.avatar") avatarButton?: HTMLButtonElement;
  @query("#mobile-menu-google-login-button-wrapper")
  mobileMenuGoogleLoginButtonWrapper?: HTMLDivElement;
  @query('#mobile-menu-google-login-button-wrapper div[role="button"]')
  mobileMenuGoogleLoginButton?: HTMLButtonElement;

  get user() {
    return this.authController.user;
  }

  get isLogged() {
    return this.authController.isLogged;
  }

  get isPremium() {
    return this.user?.isPremium || this.subscriptionController.isPremiumActive;
  }

  get isPhoneNumberSet() {
    return this.user?.phoneNumber;
  }

  get shouldShowManageSubscriptionLink() {
    return this.isPremium || this.subscriptionController.showNotification;
  }

  get unreadCount() {
    // ! TODO - Remove this condition when we add notifications to the subscription system
    let extraNotification = !this.isPhoneNumberSet ? 1 : 0;

    if (this.subscriptionController.showNotification) {
      extraNotification = extraNotification + 1;
    }

    return this.notificationsController.unreadCount + extraNotification;
  }

  get unreadSubscriptionNotificationsCount() {
    // ! TODO - Remove this condition when we add notifications to the subscription system
    // For now we are adding 1 to the count if the subscriptions state is
    // waiting for payment so users can see the notification indicator in the menu
    if (this.subscriptionController.showNotification) {
      return (
        this.notificationsController.unreadSubscriptionNotificationsCount + 1
      );
    }

    return this.notificationsController.unreadSubscriptionNotificationsCount;
  }

  get unreadAwardNotificationsCount() {
    return this.notificationsController.unreadAwardNotificationsCount;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("hideheader", this.dismissMenu.bind(this));
  }

  disconnectedCallback() {
    window.removeEventListener("hideheader", this.dismissMenu.bind(this));
    super.disconnectedCallback();
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>
  ): void {
    super.firstUpdated(_changedProperties);
    this.classList.remove("skeleton");
  }

  protected updated(_changedProperties: PropertyValues): void {
    super.updated(_changedProperties);
    window.dispatchEvent(
      new CustomEvent("profile-button-updated", {
        detail: {
          isMobileMenu: this.isMobileMenu,
          isLogged: this.isLogged,
          isPremium: this.isPremium,
        },
      })
    );
  }

  handleLogout() {
    if (!this.user) return;
    const uuid = this.user.uuid;
    const isPremium = this.user.isPremium;
    this.authController.logout();
    sendEventUserLoggedOut({ uuid, isPremium });
  }

  dismissMenu() {
    this.showMenu = false;
    this.avatarButton?.blur();
  }

  toggleMenu() {
    this.showMenu = !this.showMenu;
    sendEventTriggerUserMenu(this.showMenu);
  }

  formatCountNumber(count: number) {
    if (count === 0) return "";
    if (count > 99) return "+99";
    return count;
  }

  renderUnreadCount(count: number, isMain: boolean = false) {
    if (!count) return nothing;
    const isMini = isMain || count === 1;
    return html` <span
      class="${classMap({
        "notification-count": true,
        "notification-count--main": isMain,
        "notification-count--mini": isMini,
      })}"
    >
      ${this.formatCountNumber(isMini ? 0 : count)}
    </span>`;
  }

  renderHeaderTitle() {
    const name = this.user?.name;
    if (!name) return "Bem vindo ao Toda Matéria";
    return html`Olá ${name}`;
  }

  closeMobileMenu() {
    const navbarToggle = document.getElementById("navbarToggle");
    const header = document.getElementById("main-header");
    const navBarContainer = document.getElementById("navBarContainer");
    if (navbarToggle) {
      navbarToggle.classList.remove("active");
      navBarContainer?.classList.remove("active");
      header?.classList.remove("header--menu-active");
    }
  }

  openMobileMenu() {
    const navbarToggle = document.getElementById("navbarToggle");
    const header = document.getElementById("main-header");
    const navBarContainer = document.getElementById("navBarContainer");
    if (navbarToggle) {
      navbarToggle.classList.add("active");
      navBarContainer?.classList.add("active");
      header?.classList.add("header--menu-active");
    }
  }

  openLoginModal() {
    try {
      showLoginModal(undefined, () => {
        if (this.isMobileMenu && !this.isLogged) {
          this.openMobileMenu();
        }
      });
      if (this.isMobileMenu) {
        this.closeMobileMenu();
      }
    } catch (error) {
      window.location.href = "/login/";
    }
  }

  createFakeLoginButton() {
    if (this.mobileMenuGoogleLoginButton)
      return { click: () => this.mobileMenuGoogleLoginButton?.click() };

    const google = (window as any).google;
    const wrapper = this.mobileMenuGoogleLoginButtonWrapper;
    if (!google || !wrapper) return null;
    google.accounts.id.renderButton(wrapper, {
      type: "icon",
      theme: "outline",
      size: "medium",
      locale: "pt-BR",
    });

    if (!this.mobileMenuGoogleLoginButton) return null;
    return { click: () => this.mobileMenuGoogleLoginButton?.click() };
  }

  loginWithGoogle() {
    const fakeLoginButton = this.createFakeLoginButton();
    if (fakeLoginButton) fakeLoginButton.click();
    else this.openLoginModal();
  }

  render() {
    if (this.isMobileMenu && this.isLogged) {
      return html`
        <div class="accordion-item accordion-item--user">
          <button class="accordion-header">
            <span class="user-avatar">
              ${this.user!.picture
                ? html`<img
                    src="${this.user!.picture}"
                    alt="Avatar"
                    title="Avatar"
                    draggable="false"
                  />`
                : html`<span class="user-icon"></span>`}
            </span>
            Minha conta ${this.renderUnreadCount(this.unreadCount, true)}
            <span class="icon icon-052-chevron"></span>
          </button>
          <ul class="accordion-content">
            <li>
              <a href="/user/meu-perfil/" @click=${sendEventMyProfileClick}>
                <span class="user-icon profile-icon"></span>
                <span class="dropdown-item--text">Meu Perfil</span>
                <span class="icon icon-052-chevron"></span>
              </a>
              ${!this.isPhoneNumberSet ? this.renderUnreadCount(1) : nothing}
            </li>
            ${this.shouldShowManageSubscriptionLink
              ? html`
                  <li>
                    <a
                      href="/user/gerir-assinatura/"
                      @click=${sendEventMySubscriptionClick}
                    >
                      <span class="user-icon crown-icon"></span>
                      <span class="dropdown-item--text">Assinatura</span>
                      <span class="icon icon-052-chevron"></span>
                    </a>
                    ${this.renderUnreadCount(
                      this.unreadSubscriptionNotificationsCount
                    )}
                  </li>
                `
              : nothing}
            ${this.unreadAwardNotificationsCount
              ? html`
                  <li>
                    <a
                      href="/user/minhas-conquistas/"
                      @click=${sendEventMyAchievementsClick}
                    >
                      <span class="user-icon award-icon"></span>
                      <span class="dropdown-item--text">Minhas Conquistas</span>
                      <span class="icon icon-052-chevron"></span>
                    </a>
                    ${this.renderUnreadCount(
                      this.unreadAwardNotificationsCount
                    )}
                  </li>
                `
              : nothing}
            <li>
              <button class="btn-logout" @click=${this.handleLogout}>
                <span class="user-icon logout-icon"></span>
                <span class="dropdown-item--text">Sair</span>
                <span class="icon icon-052-chevron"></span>
              </button>
            </li>
          </ul>
        </div>
      `;
    }

    if (this.isMobileMenu && !this.isLogged) {
      return html`
        <div id="mobile-menu-google-login-button-wrapper" class="hidden"></div>
        <button
          @click=${this.loginWithGoogle}
          class="mobile-menu-login-button"
          title="Fazer Login"
        >
          <svg
            width="21"
            height="20"
            viewBox="0 0 21 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20.2992 10.2271C20.2992 9.51804 20.2356 8.83624 20.1174 8.18164H10.6992V12.0498H16.081C15.8492 13.2998 15.1447 14.3589 14.0856 15.068V17.5771H17.3174C19.2083 15.8362 20.2992 13.2725 20.2992 10.2271Z"
              fill="#4285F4"
            />
            <path
              d="M10.7001 20.0004C13.4001 20.0004 15.6637 19.1049 17.3182 17.5777L14.0864 15.0686C13.191 15.6686 12.0455 16.0231 10.7001 16.0231C8.09547 16.0231 5.89097 14.264 5.10457 11.9004H1.76367V14.4913C3.40917 17.7595 6.79097 20.0004 10.7001 20.0004Z"
              fill="#34A853"
            />
            <path
              d="M5.10372 11.8997C4.90372 11.2997 4.79012 10.6588 4.79012 9.99969C4.79012 9.34059 4.90372 8.69969 5.10372 8.09969V5.50879H1.76282C1.08562 6.85879 0.699219 8.38609 0.699219 9.99969C0.699219 11.6133 1.08562 13.1406 1.76282 14.4906L5.10372 11.8997Z"
              fill="#FBBC04"
            />
            <path
              d="M10.7001 3.9773C12.1682 3.9773 13.4864 4.4818 14.5228 5.4727L17.391 2.6045C15.6592 0.9909 13.3955 0 10.7001 0C6.79097 0 3.40917 2.2409 1.76367 5.5091L5.10457 8.1C5.89097 5.7364 8.09547 3.9773 10.7001 3.9773Z"
              fill="#E94235"
            />
          </svg>
          Entrar com Google
        </button>
      `;
    }

    if (this.isLogged) {
      return html`
        <button
          class="avatar"
          aria-expanded="${this.showMenu ? "true" : "false"}"
          aria-controls="profile-menu"
          title="Menu Perfil"
          @click=${this.toggleMenu}
        >
          <span aria-hidden="true" class="visually-hidden">Menu Perfil</span>
          ${this.user!.picture
            ? html`<img
                src="${this.user!.picture}"
                alt="Avatar"
                title="Avatar"
                draggable="false"
              />`
            : html`<span class="user-icon"></span>`}
        </button>
        ${this.renderUnreadCount(this.unreadCount, true)}
        <ul
          id="profile-menu"
          class="user-menu-dropdown"
          ?hidden="${!this.showMenu}"
        >
          <span class="curtain" @click=${this.toggleMenu}></span>
          <li class="user-menu-dropdown-header">
            <span>${this.renderHeaderTitle()}</span>
          </li>
          <li>
            <a
              class="menu-item-content"
              href="/user/meu-perfil/"
              @click=${sendEventMyProfileClick}
              ><span class="profile-icon"></span>Meu Perfil</a
            >
            ${!this.isPhoneNumberSet ? this.renderUnreadCount(1) : nothing}
          </li>
          <li>
            <a
              class="menu-item-content"
              href="/user/minhas-redacoes/"
              @click=${sendEventMyEssaysClick}
              ><span class="doc-write-icon"></span>Minhas Redações</a
            >
          </li>
          <li>
            <a
              class="menu-item-content"
              href="/user/conteudos-favoritos/"
              @click=${sendEventMyFavoritesClick}
              ><span class="favorites-icon"></span>Meus Favoritos</a
            >
          </li>
          ${this.shouldShowManageSubscriptionLink
            ? html`
                <li>
                  <a
                    class="menu-item-content"
                    href="/user/gerir-assinatura/"
                    @click=${sendEventMySubscriptionClick}
                    ><span class="crown-icon"></span>Assinatura</a
                  >
                  ${this.renderUnreadCount(
                    this.unreadSubscriptionNotificationsCount
                  )}
                </li>
              `
            : nothing}
          <li>
            <a
              class="menu-item-content"
              href="/user/minhas-conquistas/"
              @click=${sendEventMyAchievementsClick}
              ><span class="award-icon"></span>Minhas Conquistas</a
            >
            ${this.renderUnreadCount(this.unreadAwardNotificationsCount)}
          </li>
          <li>
            <button class="menu-item-content" @click=${this.handleLogout}>
              <span class="logout-icon"></span>Sair
            </button>
          </li>
          <li>
            <a
              class="menu-item-banner"
              href="https://tiddly.xyz/xudXD"
              target="_blank"
            >
              <span class="whatsapp-icon"></span>
              <span class="cta-copy"
                >Quer passar no ENEM?<br />
                Junte-se ao nosso canal!</span
              >
            </a>
          </li>
        </ul>
      `;
    }

    return html`
      <button
        @click="${this.openLoginModal}"
        class="avatar login-button"
        title="Fazer Login"
      >
        <span class="user-icon"></span>
      </button>
    `;
  }
}
